import type { Theme, ThemeOptions } from '@mui/material/styles';
import { accordionThemeComponents } from './Accordion/Accordion.theme';
import { buttonThemeComponents } from './Button/Button.theme';
import { checkboxThemeComponents } from './Checkbox/Checkbox.theme';
import { chipThemeComponents } from './Chip/Chip.theme';
import { iconButtonThemeComponents } from './IconButton/IconButton.theme';
import { iconThemeComponents } from './Icon/Icon.theme';
import { datePickerThemeComponents } from './DatePicker/DatePicker.theme';
import { dialogThemeComponents } from './Dialog/Dialog.theme';
import { timePickerThemeComponents } from './TimePicker/TimePicker.theme';
import { autocompleteThemeComponents } from './Autocomplete/Autocomplete.theme';
import { inputThemeComponents } from './Input/Input.theme';
import { selectThemeComponents } from './Select/Select.theme';
import { radioThemeComponents } from './Radio/Radio.theme';
import { ratingThemeComponents } from './Rating/Rating.theme';
import { stepperThemeComponents } from './Stepper/Stepper.theme';
import { switchThemeComponents } from './Switch/Switch.theme';
import { toggleButtonGroupThemeComponents } from './ToggleButtonGroup/ToggleButtonGroup.theme';
import { typographyThemeComponents } from './Typography/Typography.theme';

/** Merge all design-system `components` overrides for use in Storybook or an app `createTheme(base, { components })`. */
export function uiComponentsThemeComponents(theme: Theme): NonNullable<ThemeOptions['components']> {
  return mergeMuiComponents(
    accordionThemeComponents(theme),
    buttonThemeComponents(theme),
    checkboxThemeComponents(theme),
    chipThemeComponents(theme),
    iconButtonThemeComponents(theme),
    iconThemeComponents(theme),
    datePickerThemeComponents(theme),
    dialogThemeComponents(theme),
    timePickerThemeComponents(theme),
    inputThemeComponents(theme),
    autocompleteThemeComponents(theme),
    selectThemeComponents(theme),
    radioThemeComponents(theme),
    ratingThemeComponents(theme),
    typographyThemeComponents(theme),
    stepperThemeComponents(theme),
    switchThemeComponents(theme),
    toggleButtonGroupThemeComponents(theme)
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && (value as { constructor?: unknown }).constructor === Object;
}

/** Deep-merge `ThemeOptions['components']` so slices can safely target the same MUI component. */
function mergeMuiComponents(
  ...slices: Array<ThemeOptions['components'] | undefined>
): NonNullable<ThemeOptions['components']> {
  const result: Record<string, unknown> = {};

  for (const slice of slices) {
    if (!slice) continue;
    for (const key of Object.keys(slice)) {
      const existing = result[key];
      const incoming = (slice as Record<string, unknown>)[key];
      result[key] = deepMerge(existing, incoming);
    }
  }

  return result as NonNullable<ThemeOptions['components']>;
}

function deepMerge(a: unknown, b: unknown): unknown {
  if (isPlainObject(a) && isPlainObject(b)) {
    const out: Record<string, unknown> = { ...a };
    for (const k of Object.keys(b)) {
      out[k] = deepMerge(a[k], b[k]);
    }
    return out;
  }
  return b ?? a;
}
